/**
 * Spodní lišta na telefonu, pro všechny role.
 *
 * Do 2. 9. 2026 měl vlastní spodní lištu jenom trenérský portál, zbytek adminu
 * se na telefonu ovládal hamburgerem a vysouvací zásuvkou. Pro ownera to
 * znamenalo, že se ke Cashflow proklikával přes menu, které zabralo celou
 * obrazovku. Lišta je proto teď jedna pro celou aplikaci: čtyři nejčastější
 * sekce role a pod „Více“ sheet se VŠEMI ostatními, poskládanými do stejných
 * skupin jako v postranním panelu. Nikdo tak nepřijde o žádnou sekci.
 *
 * Seznam položek přichází z `src/lib/menu.js`, tedy ze stejného zdroje, jaký
 * čte postranní panel. Hlídá to `scripts/check-menu-role.mjs`.
 *
 * Lišta se schová, když je vysunutá klávesnice (třída `klavesnice-nahore` na
 * body, viz src/index.css a src/lib/klavesnice.js), a sedí nad home
 * indikátorem díky `safe-bottom`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MoreHorizontal, X, Search, LogOut, ChevronDown, Lock, CircleDot, UserCog } from "lucide-react";
import { logout, getCurrentUser } from "../lib/auth.js";
import { apiFetch } from "../lib/api.js";
import { polozkyProRoli, mobilniPolozky } from "../lib/menu.js";
import { useMenuOdznaky, popisOdznaku } from "../lib/menuOdznaky.js";


/** Hledání bez diakritiky: „vypl“ najde Výplaty. */
const bezDiakritiky = (t) =>
  String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function SpodniLista() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const role = user?.role || "";
  const realRole = user?._realRole || "";

  const [otevreno, setOtevreno] = useState(false);
  const [hledani, setHledani] = useState("");
  const [rucneOtevrene, setRucneOtevrene] = useState({});

  // Stejná pravidla jako v postranním panelu: ukončený trenér vidí jen
  // Fakturaci a kalendář rezervací se trenérům zapíná postupně.
  const [trenerUkoncen, setTrenerUkoncen] = useState(false);
  // 14.9.2026 (fáze 6): trenér startuje se schovaným kalendářem, ať se
  // položka neukáže před odpovědí API.
  const [kalendarZapnuty, setKalendarZapnuty] = useState(role !== "trener");
  useEffect(() => {
    if (role !== "trener") { setTrenerUkoncen(false); setKalendarZapnuty(true); return undefined; }
    const jeOwner = realRole === "owner";
    let url = "/api/my-trainer";
    if (jeOwner) {
      let va = "";
      try { va = localStorage.getItem("fw40_view_as_trener") || ""; } catch { /* ignoruj */ }
      if (!va) { setTrenerUkoncen(false); return undefined; }
      url = "/api/my-trainer?jmeno=" + encodeURIComponent(va);
    }
    let zij = true;
    apiFetch(url).then((r) => r.json()).then((j) => {
      if (!zij) return;
      setTrenerUkoncen(/ukon/i.test(String(j?.trainer?.status || "")) || j?.trainer?.active === false);
      // 10.9.2026: viz Sidebar — náhled ownera drží skutečný stav trenéra.
      setKalendarZapnuty(j?.rezervace_kalendar === true);
    }).catch(() => {});
    return () => { zij = false; };
  }, [role, realRole, location.pathname]);

  // 7.9.2026: stejně jako v sidebaru — trenér bez přiřazeného běhu Bootcampy
  // v liště nemá. Jeden lehký dotaz, jen pro roli trenér.
  const [maBootcamp, setMaBootcamp] = useState(false);
  useEffect(() => {
    if (role !== "trener") { setMaBootcamp(false); return; }
    let zij = true;
    // 1.10.2026: viz Sidebar — v náhledu ownera se ptáme za zvoleného trenéra.
    let jakoTrener = "";
    try { jakoTrener = localStorage.getItem("fw40_view_as_trener") || ""; } catch { /* ignoruj */ }
    const urlBc = "/api/bootcampy?mujBeh=1" + (jakoTrener ? "&jmeno=" + encodeURIComponent(jakoTrener) : "");
    apiFetch(urlBc).then((r) => r.json())
      .then((j) => { if (zij) setMaBootcamp(!!(j && j.maBeh)); })
      .catch(() => {});
    return () => { zij = false; };
  }, [role]);

  // Odznaky (počty) u položek menu. 1. 11. 2026: stejný sdílený zdroj jako
  // postranní panel (lib/menuOdznaky.js), dřív tu byl jen počet absencí.
  const odznaky = useMenuOdznaky();
  const pocet = (polozka) => (polozka.badgeKey ? Number(odznaky[polozka.badgeKey]) || 0 : 0);

  const ctx = useMemo(
    () => ({ realRole, trenerUkoncen, kalendarZapnuty, maBootcamp }),
    [realRole, trenerUkoncen, kalendarZapnuty, maBootcamp]
  );

  const hlavni = useMemo(
    () => mobilniPolozky(role, location.pathname, ctx),
    [role, location.pathname, ctx]
  );
  const vsechno = useMemo(() => polozkyProRoli(role, ctx), [role, ctx]);

  // Do sheetu jde všechno, co není v hlavní čtveřici.
  const vHlavni = new Set(hlavni.map((p) => p.to));
  const dalsi = vsechno
    .map((item) => {
      if (!item.children) return vHlavni.has(item.to) ? null : item;
      const zbytek = item.children.filter((c) => !vHlavni.has(c.to));
      return zbytek.length ? { ...item, children: zbytek } : null;
    })
    .filter(Boolean);
  // Číslo u „Více“ = součet odznaků všeho, co je schované v sheetu.
  const pocetVice = dalsi.reduce(
    (n, item) => n + (item.children ? item.children.reduce((m, c) => m + pocet(c), 0) : pocet(item)),
    0
  );

  // Skupina, ve které leží otevřená stránka, se rozbalí sama.
  const aktivniSkupina = vsechno.find(
    (i) => i.children && i.children.some((c) => location.pathname.startsWith(c.to))
  )?.label;

  // Zavřít při přechodu jinam a uklidit hledání.
  useEffect(() => { setOtevreno(false); setHledani(""); }, [location.pathname]);

  // Zamknout rolování pod otevřeným sheetem. Samotné overflow: hidden iOS
  // Safari ignoruje, drží až position: fixed; posun se zapamatuje a vrátí.
  useEffect(() => {
    if (!otevreno) return undefined;
    const y = window.scrollY;
    const b = document.body;
    const puvodni = { overflow: b.style.overflow, position: b.style.position, top: b.style.top, width: b.style.width };
    b.style.overflow = "hidden";
    b.style.position = "fixed";
    b.style.top = `-${y}px`;
    b.style.width = "100%";
    const naEsc = (e) => e.key === "Escape" && setOtevreno(false);
    document.addEventListener("keydown", naEsc);
    return () => {
      document.removeEventListener("keydown", naEsc);
      Object.assign(b.style, puvodni);
      window.scrollTo(0, y);
    };
  }, [otevreno]);

  const odhlasit = async () => { await logout(); navigate("/login"); };

  // Hledá se napříč skupinami. Dokud je pole prázdné, platí sbalené skupiny.
  const dotaz = bezDiakritiky(hledani.trim());
  const najdene = dotaz
    ? dalsi.flatMap((item) => (item.children ? item.children : [item]))
        .filter((p) => bezDiakritiky(p.label).includes(dotaz))
    : null;

  if (!role) return null;

  return (
    <>
      <nav
        data-spodni-lista
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 flex border-t border-brand/10 bg-white/95 backdrop-blur-md lg:hidden"
      >
        {hlavni.map((p) => {
          const Ikona = p.icon || CircleDot;
          return (
            <NavLink
              key={p.to}
              to={p.to}
              className={({ isActive }) =>
                `flex min-h-[68px] min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-xs font-semibold transition ${
                  isActive ? "text-brand" : "text-slate-500"
                }`
              }
            >
              <span className="relative">
                <Ikona className="h-7 w-7" />
                <OdznakIkony pocet={pocet(p)} title={p.badgeKey ? popisOdznaku(p.badgeKey, pocet(p)) : undefined} />
              </span>
              <span className="truncate">{p.label}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => { setHledani(""); setOtevreno(true); }}
          className={`flex min-h-[68px] min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-xs font-semibold transition ${
            otevreno ? "text-brand" : "text-slate-500"
          }`}
          aria-label="Další sekce"
        >
          <span className="relative">
            <MoreHorizontal className="h-7 w-7" />
            <OdznakIkony pocet={pocetVice} />
          </span>
          <span className="truncate">Více</span>
        </button>
      </nav>

      {otevreno && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Další sekce">
          <div className="absolute inset-0 animate-fade-in bg-brand-dark/40" onClick={() => setOtevreno(false)} />
          {/* Vysoký podle obsahu, nejvýš 92 % obrazovky: trenérovi s pěti
              položkami zůstane malý, ownerovi se roztáhne přes celou výšku. */}
          <div className="safe-bottom absolute inset-x-0 bottom-0 flex max-h-[calc(var(--vv-vyska,100vh)*0.92)] animate-sheet-up flex-col rounded-t-2xl bg-white p-4 shadow-2xl">
            <div className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-slate-300" />
            <div className="mb-2 flex shrink-0 items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Další sekce</p>
              <button type="button" onClick={() => setOtevreno(false)} className="btn-ikona" aria-label="Zavřít">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative mb-3 shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="field !pl-9"
                value={hledani}
                onChange={(e) => setHledani(e.target.value)}
                placeholder="Najít sekci"
                aria-label="Najít sekci"
                data-hledani-sekce
              />
            </div>

            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain">
              {najdene ? (
                najdene.length ? (
                  najdene.map((p) => <OdkazSekce key={p.to} polozka={p} pocet={pocet(p)} />)
                ) : (
                  <p className="px-3 py-6 text-center text-sm text-slate-500">Nic takového tu není.</p>
                )
              ) : (
                dalsi.map((item) => {
                  if (!item.children) return <OdkazSekce key={item.to} polozka={item} pocet={pocet(item)} />;
                  const rozbalena = rucneOtevrene[item.label] ?? item.label === aktivniSkupina;
                  const IkonaSkupiny = item.icon || CircleDot;
                  return (
                    <div key={item.label}>
                      <button
                        type="button"
                        aria-expanded={rozbalena}
                        data-skupina-mobil={item.label}
                        onClick={() => setRucneOtevrene((s) => ({ ...s, [item.label]: !rozbalena }))}
                        className="flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 text-base font-semibold text-slate-800 transition hover:bg-brand-light"
                      >
                        <IkonaSkupiny className="h-5 w-5 shrink-0 text-brand" />
                        <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                        {!rozbalena && item.children.reduce((n, c) => n + pocet(c), 0) > 0 && (
                          <span className="shrink-0 rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold tabular-nums text-white">
                            {item.children.reduce((n, c) => n + pocet(c), 0)}
                          </span>
                        )}
                        <ChevronDown className={`h-6 w-6 shrink-0 text-slate-500 transition-transform ${rozbalena ? "" : "-rotate-90"}`} />
                      </button>
                      {rozbalena && (
                        <div className="ml-4 border-l border-slate-200 pl-2">
                          {item.children.map((c) => <OdkazSekce key={c.to} polozka={c} pocet={pocet(c)} />)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Odhlásit je patka sheetu, neroluje a nikdy nekončí pod home indikátorem. */}
            <div className="shrink-0 space-y-2 pt-2">
              <NavLink
                to="/muj-ucet"
                className="flex min-h-[48px] items-center gap-3 rounded-xl px-3 text-base font-semibold text-slate-800 transition hover:bg-brand-light"
              >
                <UserCog className="h-5 w-5 shrink-0 text-brand" /> Můj účet
              </NavLink>
              <button
                type="button"
                onClick={odhlasit}
                className="flex min-h-[48px] w-full items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 text-base font-semibold text-rose-700"
              >
                <LogOut className="h-5 w-5 shrink-0" /> Odhlásit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Červené číslo v rohu ikony ve spodní liště (stejný vzhled jako dřív u Absencí). */
function OdznakIkony({ pocet = 0, title }) {
  if (!(pocet > 0)) return null;
  return (
    <span
      title={title}
      className="absolute -right-2 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold leading-none text-white"
    >
      {pocet}
    </span>
  );
}

/** Jedna sekce v sheetu. Zamčená položka se nedá otevřít, jen se ukáže. */
function OdkazSekce({ polozka, pocet = 0 }) {
  const Ikona = polozka.icon || CircleDot;
  if (polozka.locked) {
    return (
      <div
        title="Zamčeno, zatím nedostupné"
        className="flex min-h-[48px] cursor-not-allowed select-none items-center gap-3 rounded-xl px-3 text-base font-semibold text-slate-400"
      >
        <Ikona className="h-5 w-5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{polozka.label}</span>
        <Lock className="h-4 w-4 shrink-0" />
      </div>
    );
  }
  return (
    <NavLink
      to={polozka.to}
      className={({ isActive }) =>
        `flex min-h-[48px] items-center gap-3 rounded-xl px-3 text-base font-semibold transition ${
          isActive ? "bg-brand text-white" : "text-slate-800 hover:bg-brand-light"
        }`
      }
    >
      <Ikona className="h-5 w-5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{polozka.label}</span>
      {pocet > 0 && (
        <span className="shrink-0 rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold tabular-nums text-white" title={polozka.badgeKey ? popisOdznaku(polozka.badgeKey, pocet) : String(pocet)}>
          {pocet}
        </span>
      )}
    </NavLink>
  );
}
